import { useEffect, useState } from "react";
import { useReviewLink } from "@/lib/siteContent";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Gift, LogOut, ArrowRight, Calendar, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPost } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/landing/Logo";
import { fmt12 } from "@/lib/time";

const STATUS_STYLE = {
  pending: { color: "text-amber-400", Icon: Clock, label: "Pending payment" },
  confirmed: { color: "text-emerald-400", Icon: CheckCircle2, label: "Confirmed" },
  completed: { color: "text-sky-400", Icon: CheckCircle2, label: "Completed" },
  cancelled: { color: "text-red-400", Icon: XCircle, label: "Cancelled" },
};

export default function AccountPage() {
  const { user, loading, logout, checkAuth, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState([]);

  useEffect(() => {
    if (!loading && !user) {
      // Not authenticated, send to login
      // small delay to avoid flicker
    }
  }, [loading, user]);

  const load = async () => {
    try {
      const [b, c] = await Promise.all([apiGet("/bookings/me"), apiGet("/cars/me")]);
      setBookings(b.bookings || []);
      setCars(c.cars || []);
    } catch (err) {
      console.warn("Failed to load account data:", err);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  // Square payment return (legacy ?payment=success path from old Stripe flow). The new flow
  // confirms payment inline before navigating to /account, so just clear the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get("payment");
    if (payment) {
      if (payment === "success") {
        toast.success("Booking confirmed! See you soon.");
      } else if (payment === "cancel") {
        toast("Payment cancelled");
      }
      window.history.replaceState(null, "", "/account");
    }
  }, [location.search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <h1 className="text-3xl font-['Outfit']">Sign in to your account</h1>
          <p className="text-gray-400">
            Get 10% off your first detail. Save your cars + booking history.
          </p>
          <Button
            data-testid="login-cta"
            onClick={() => navigate("/login?next=/account")}
            className="bg-sky-500 hover:bg-sky-400 text-white w-full"
          >
            Sign in / Create account
          </Button>
          <button
            onClick={() => startGoogleLogin("/account")}
            className="text-sm text-gray-400 hover:text-white block w-full"
          >
            Or continue with Google
          </button>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-gray-500 hover:text-white"
          >
            ← Back to home
          </button>
        </div>
      </div>
    );
  }

  const loyalty = user.loyalty || { paid_count: 0, next_reward_at: 5, progress_pct: 0, reward_ready: false };
  const stepsCompleted = loyalty.paid_count % loyalty.next_reward_at;
  const stepsRemaining = loyalty.next_reward_at - stepsCompleted;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-black/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center"
            data-testid="account-logo"
          >
            <Logo size={36} showWordmark wordmarkClassName="text-xl" />
          </button>
          <div className="flex items-center gap-4">
            {user.picture && (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            )}
            <span className="text-sm text-gray-300 hidden sm:block">{user.name || user.email}</span>
            <Button
              variant="ghost"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              data-testid="logout-btn"
              className="text-gray-400 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {/* Welcome + loyalty bar */}
        <section>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Outfit'] text-4xl mb-2"
          >
            Welcome back, <span className="text-sky-400">{user.name?.split(" ")[0] || "there"}</span>
          </motion.h1>
          <p className="text-gray-400 mb-8">
            {loyalty.paid_count} {loyalty.paid_count === 1 ? "detail" : "details"} completed
          </p>

          <motion.div
            className="relative rounded-2xl p-6 bg-gradient-to-br from-sky-500/10 via-transparent to-transparent border border-sky-500/20 overflow-hidden"
            data-testid="loyalty-card"
            whileHover={{ scale: 1.005 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <motion.div
                  animate={loyalty.reward_ready ? { rotate: [0, -8, 8, -8, 0], scale: [1, 1.1, 1.1, 1.1, 1] } : {}}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 1.5 }}
                >
                  <Gift className={`w-6 h-6 ${loyalty.reward_ready ? "text-amber-300" : "text-sky-400"}`} />
                </motion.div>
                <div>
                  <p className="text-white font-medium">
                    {loyalty.reward_ready
                      ? "Your free interior detail is ready!"
                      : `${stepsRemaining} ${stepsRemaining === 1 ? "detail" : "details"} until your free reward`}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Get a free interior detail after every {loyalty.next_reward_at} paid services
                  </p>
                </div>
              </div>
              <motion.p
                key={stepsCompleted}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-sky-300 font-['Outfit'] text-2xl"
              >
                {stepsCompleted}/{loyalty.next_reward_at}
              </motion.p>
            </div>

            {/* Animated dotted progress with stamps */}
            <div className="relative">
              {/* Track */}
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-white/5" />

              {/* Filled bar */}
              <motion.div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-gradient-to-r from-sky-500 via-sky-400 to-sky-300"
                initial={{ width: 0 }}
                animate={{ width: `${(stepsCompleted / loyalty.next_reward_at) * 100}%` }}
                transition={{ duration: 1.2, ease: [0.25, 0.4, 0.25, 1] }}
              />

              {/* Glow */}
              {stepsCompleted > 0 && (
                <motion.div
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-2 rounded-full"
                  style={{
                    background: "linear-gradient(90deg, transparent, rgba(14,165,233,0.6), transparent)",
                    width: 60,
                    filter: "blur(6px)",
                  }}
                  animate={{ x: ["-10%", `${(stepsCompleted / loyalty.next_reward_at) * 100}%`] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                />
              )}

              {/* Step stamps */}
              <div className="relative flex items-center justify-between">
                {Array.from({ length: loyalty.next_reward_at }).map((_, i) => {
                  const reached = i < stepsCompleted;
                  const isFinal = i === loyalty.next_reward_at - 1;
                  return (
                    <motion.div
                      key={i}
                      title={isFinal ? "Free interior detail" : `Detail #${i + 1}`}
                      data-testid={`loyalty-step-${i}`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1, rotate: reached ? 360 : 0 }}
                      transition={{
                        delay: 0.1 + i * 0.08,
                        type: "spring",
                        stiffness: 220,
                        damping: 14,
                      }}
                      whileHover={{ scale: 1.18, y: -2 }}
                      className={`relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold border-2 cursor-default transition-colors ${
                        reached
                          ? "bg-sky-500 border-sky-300 text-white shadow-[0_0_20px_rgba(14,165,233,0.6)]"
                          : isFinal
                          ? "bg-black border-amber-400/40 text-amber-300"
                          : "bg-black border-white/20 text-gray-500"
                      }`}
                    >
                      {isFinal ? (
                        <Gift className="w-4 h-4" />
                      ) : reached ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        i + 1
                      )}
                      {reached && (
                        <motion.div
                          className="absolute inset-0 rounded-full bg-sky-400/30"
                          initial={{ scale: 1, opacity: 0.8 }}
                          animate={{ scale: 2, opacity: 0 }}
                          transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.15 }}
                        />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {loyalty.reward_ready && (
              <Button
                className="mt-5 bg-amber-400 hover:bg-amber-300 text-black font-semibold"
                onClick={() => navigate("/")}
              >
                Redeem free interior detail <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </motion.div>
        </section>

        {/* Bookings history */}
        <section>
          <h2 className="font-['Outfit'] text-2xl mb-4">Your detail history</h2>
          {bookings.length === 0 ? (
            <div className="rounded-xl border border-white/10 p-8 text-center text-gray-500">
              No bookings yet — get a free quote to get started
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.map((b) => {
                const st = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
                const Icon = st.Icon;
                const proposal = b.reschedule_proposal;
                const isOpen = b.status !== "completed" && b.status !== "cancelled";
                // Only show admin proposal banner to customer (not their own pending request)
                const showAdminProposal = proposal && proposal.proposed_by === "admin";
                const showOwnProposalSent = proposal && proposal.proposed_by === "customer";

                return (
                  <div
                    key={b.booking_id}
                    data-testid={`booking-${b.booking_id}`}
                    className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className={`flex items-center gap-2 text-xs uppercase tracking-widest mb-2 ${st.color}`}>
                          <Icon className="w-3.5 h-3.5" /> {st.label}
                        </div>
                        <p className="text-white font-medium">
                          {b.line_items.map((it) => it.name).join(" + ")}
                        </p>
                        <p className="text-sm text-gray-400 mt-1 flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5" />
                          {b.scheduled_date} · {fmt12(b.scheduled_slot)}
                        </p>
                        {b.vehicle && (b.vehicle.make || b.vehicle.model) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {[b.vehicle.year, b.vehicle.color, b.vehicle.make, b.vehicle.model].filter(Boolean).join(" ")}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sky-400 font-semibold text-lg">${b.total.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">
                          Deposit ${b.deposit.toFixed(2)} {b.payment_status === "paid" ? "✓ paid" : "unpaid"}
                        </p>
                      </div>
                    </div>

                    {isOpen && !proposal && (
                      <div className="flex gap-2 flex-wrap">
                        <BookingActions booking={b} onChanged={load} />
                      </div>
                    )}

                    {b.status === "completed" && (
                      <ReviewBlock booking={b} onChanged={load} />
                    )}

                    {showAdminProposal && (
                      <RescheduleBanner
                        booking={b}
                        proposal={proposal}
                        onChanged={load}
                      />
                    )}

                    {showOwnProposalSent && (
                      <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 text-sm flex items-center justify-between gap-3">
                        <div>
                          <p className="text-amber-200 font-medium">
                            Reschedule request sent — waiting for Reflections to confirm
                          </p>
                          <p className="text-xs text-amber-200/70 mt-0.5">
                            Proposed: {proposal.new_date} · {fmt12(proposal.new_slot)}
                          </p>
                        </div>
                        <Button
                          data-testid={`withdraw-${b.booking_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await apiPost(`/bookings/${b.booking_id}/reschedule/decline`, {});
                              toast("Request withdrawn");
                              load();
                            } catch (err) {
                              console.warn("Withdraw failed:", err);
                              toast.error("Could not withdraw request");
                            }
                          }}
                          className="text-gray-400 hover:text-white hover:bg-white/5"
                        >
                          Withdraw
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Garage */}
        <section>
          <h2 className="font-['Outfit'] text-2xl mb-4 flex items-center gap-2">
            <Car className="w-5 h-5 text-sky-400" /> Your garage
          </h2>
          {cars.length === 0 ? (
            <div className="rounded-xl border border-white/10 p-8 text-center text-gray-500">
              Your cars will appear here after your first booking
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cars.map((c) => (
                <div
                  key={c.car_id}
                  data-testid={`car-${c.car_id}`}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
                >
                  <Car className="w-6 h-6 text-sky-500 mb-3" />
                  <p className="text-white font-medium">
                    {[c.year, c.make, c.model].filter(Boolean).join(" ") || "Vehicle"}
                  </p>
                  {c.color && <p className="text-sm text-gray-400">{c.color}</p>}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function ReviewBlock({ booking, onChanged }) {
  const reviewLink = useReviewLink();
  const [open, setOpen] = useState(false);
  if (booking.reviewed) {
    return (
      <div className="text-xs text-emerald-300 flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5" /> You reviewed this detail — thank you!
      </div>
    );
  }
  return (
    <>
      <Button
        size="sm"
        data-testid={`review-${booking.booking_id}`}
        onClick={() => setOpen(true)}
        className="bg-sky-500/15 text-sky-300 hover:bg-sky-500/25"
      >
        Leave a review
      </Button>
      {reviewLink && (
        <a href={reviewLink} target="_blank" rel="noreferrer" data-testid={`google-review-${booking.booking_id}`} className="ml-2 text-xs text-sky-300 underline">
          Review us on Google
        </a>
      )}
      {open && (
        <ReviewModal
          booking={booking}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); onChanged(); }}
        />
      )}
    </>
  );
}

function ReviewModal({ booking, onClose, onSaved }) {
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const fileToDataUrl = (f) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });

  const submit = async () => {
    if (body.trim().length < 5) {
      toast.error("Please write a few words");
      return;
    }
    setLoading(true);
    try {
      const media = await Promise.all(
        files.slice(0, 6).map(async (f) => ({
          type: f.type.startsWith("video") ? "video" : "image",
          data_url: await fileToDataUrl(f),
        }))
      );
      await apiPost("/reviews", {
        booking_id: booking.booking_id,
        rating,
        body,
        media,
      });
      toast.success("Thanks for the review!");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-md rounded-2xl border border-sky-500/30 bg-[#0d0d0d] p-6 space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <h3 className="font-['Outfit'] text-2xl">How did we do?</h3>
        <p className="text-sm text-gray-400">
          {booking.line_items.map((it) => it.name).join(" + ")} · {booking.scheduled_date}
        </p>

        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-2">Rating</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n)}
                data-testid={`review-star-${n}`}
                className="p-1"
              >
                <svg
                  viewBox="0 0 24 24"
                  className={`w-8 h-8 transition-colors ${
                    n <= rating ? "fill-sky-500 text-sky-500" : "fill-transparent text-gray-600"
                  }`}
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <polygon points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9" />
                </svg>
              </button>
            ))}
          </div>
        </div>

        <Textarea
          placeholder="Tell other customers about your experience..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="bg-white/[0.03] border-white/10 text-white"
          data-testid="review-body"
        />

        <div>
          <label className="block border-2 border-dashed border-white/15 rounded-xl p-4 text-center cursor-pointer hover:border-sky-500/50 transition-colors">
            <p className="text-white text-sm">Add photos / videos</p>
            <p className="text-gray-500 text-xs mt-0.5">Up to 6 attachments, max 8 MB each</p>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 6))}
              data-testid="review-files"
            />
          </label>
          {files.length > 0 && (
            <div className="grid grid-cols-6 gap-1 mt-2">
              {files.map((f, i) => (
                <div key={i} className="aspect-square rounded bg-white/5 overflow-hidden">
                  {f.type.startsWith("video") ? (
                    <video src={URL.createObjectURL(f)} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={URL.createObjectURL(f)} className="w-full h-full object-cover" alt="" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
          <Button
            onClick={submit}
            disabled={loading}
            data-testid="review-submit"
            className="bg-sky-500 hover:bg-sky-400 text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit review"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function BookingActions({ booking, onChanged }) {
  const [showReschedule, setShowReschedule] = useState(false);
  const [busy, setBusy] = useState(false);

  const cancel = async () => {
    if (!window.confirm("Cancel this booking? Your deposit is non-refundable.")) return;
    setBusy(true);
    try {
      await apiPost(`/bookings/${booking.booking_id}/cancel`, {});
      toast.success("Booking cancelled");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        data-testid={`customer-reschedule-${booking.booking_id}`}
        onClick={() => setShowReschedule(true)}
        className="text-sky-400 hover:bg-sky-500/10"
      >
        <Calendar className="w-3.5 h-3.5 mr-1" /> Request reschedule
      </Button>
      <Button
        size="sm"
        variant="ghost"
        data-testid={`customer-cancel-${booking.booking_id}`}
        onClick={cancel}
        disabled={busy}
        className="text-red-400 hover:bg-red-500/10"
      >
        Cancel
      </Button>
      {showReschedule && (
        <CustomerRescheduleModal
          booking={booking}
          onClose={() => setShowReschedule(false)}
          onSaved={() => { setShowReschedule(false); onChanged(); }}
        />
      )}
    </>
  );
}

function CustomerRescheduleModal({ booking, onClose, onSaved }) {
  const [newDate, setNewDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 10);
  });
  const [newSlot, setNewSlot] = useState("");
  const [slots, setSlots] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!newDate) return;
    apiGet("/slots", { date: newDate }).then((d) => setSlots(d.slots));
  }, [newDate]);

  const submit = async () => {
    if (!newDate || !newSlot) {
      toast.error("Pick a date and time");
      return;
    }
    setLoading(true);
    try {
      await apiPost(`/bookings/${booking.booking_id}/reschedule-request`, {
        new_date: newDate,
        new_slot: newSlot,
        message,
        valid_hours: 72,
      });
      toast.success("Reschedule request sent");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-md rounded-2xl border border-sky-500/30 bg-[#0d0d0d] p-6 space-y-4"
      >
        <h3 className="font-['Outfit'] text-2xl">Request a new time</h3>
        <p className="text-sm text-gray-400">
          Currently: <span className="text-white">{booking.scheduled_date} · {fmt12(booking.scheduled_slot)}</span>
        </p>
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">Preferred date</label>
          <Input
            type="date"
            value={newDate}
            onChange={(e) => { setNewDate(e.target.value); setNewSlot(""); }}
            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
            className="bg-white/[0.03] border-white/10 text-white"
            data-testid="customer-reschedule-date"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-2">Available slots</label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {slots.length === 0 ? (
              <p className="col-span-3 sm:col-span-5 text-gray-500 text-sm">No slots open this day — pick another</p>
            ) : slots.map((s) => (
              <button
                key={s}
                onClick={() => setNewSlot(s)}
                data-testid={`customer-reschedule-slot-${s}`}
                className={`py-2 rounded text-sm transition-colors ${
                  newSlot === s ? "bg-sky-500 text-white" : "bg-white/[0.03] text-gray-300 hover:bg-white/10"
                }`}
              >
                {fmt12(s)}
              </button>
            ))}
          </div>
        </div>
        <Textarea
          placeholder="Reason (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="bg-white/[0.03] border-white/10 text-white"
        />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
          <Button
            onClick={submit}
            disabled={loading}
            data-testid="customer-reschedule-submit"
            className="bg-sky-500 hover:bg-sky-400 text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function RescheduleBanner({ booking, proposal, onChanged }) {
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      await apiPost(`/bookings/${booking.booking_id}/reschedule/accept`, {});
      toast.success("New date confirmed!");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept");
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await apiPost(`/bookings/${booking.booking_id}/reschedule/decline`, {});
      toast("Proposal declined — please contact us to find another time");
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`reschedule-banner-${booking.booking_id}`}
      className="rounded-xl border border-sky-500/30 bg-gradient-to-r from-sky-500/10 to-transparent p-4"
    >
      <div className="flex items-start gap-3">
        <Calendar className="w-5 h-5 text-sky-400 mt-1 shrink-0" />
        <div className="flex-1">
          <p className="text-sky-200 text-sm font-medium">
            Reflections proposed a new time for this booking
          </p>
          <p className="text-white text-base mt-1">
            <span className="line-through text-gray-500">{proposal.old_date} · {fmt12(proposal.old_slot)}</span>
            {" → "}
            <span className="text-sky-300 font-semibold">{proposal.new_date} · {fmt12(proposal.new_slot)}</span>
          </p>
          {proposal.message && (
            <p className="text-xs text-gray-400 italic mt-1.5">"{proposal.message}"</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button
          data-testid={`reschedule-accept-${booking.booking_id}`}
          onClick={accept}
          disabled={busy}
          className="bg-sky-500 hover:bg-sky-400 text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve new time"}
        </Button>
        <Button
          data-testid={`reschedule-decline-${booking.booking_id}`}
          onClick={decline}
          disabled={busy}
          variant="ghost"
          className="text-gray-400 hover:text-white hover:bg-white/5"
        >
          Decline
        </Button>
      </div>
    </div>
  );
}
